import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Settings,
  User,
} from "lucide-react";
import { clearUserState } from "../Redux/userSlice";
import { fetchCompanyProfile, logoutCompany } from "../Redux/companySlice";

const UserProfileMenu = ({
  variant = "icon",
  className = "",
  showDashboardAction = true,
  showSettingsAction = true,
  collapsed = false,
  closeMobileSidebar,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileImageCandidateIndex, setProfileImageCandidateIndex] = useState(0);
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector((state) => state.user?.data || state.user?.user || null);
  const company = useSelector((state) => state.company?.company || null);

  const hasValidToken = !!localStorage.getItem("token");
  const isLoggedIn = hasValidToken && (user?._id || company?._id);
  const isCompany = hasValidToken && !!company;

  useEffect(() => {
    const fetchProfile = async () => {
      if (isCompany && !company?.email) {
        try {
          await dispatch(fetchCompanyProfile()).unwrap();
        } catch (error) {
          console.error("Failed to fetch company profile:", error);
        }
      }
    };

    fetchProfile();
  }, [company?.email, dispatch, isCompany]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const getImageCandidates = (candidates) => {
    const uniqueCandidates = new Set();
    candidates.forEach((candidate) => {
      if (typeof candidate === "string" && candidate.trim().length > 0) {
        uniqueCandidates.add(candidate);
      }
    });
    return Array.from(uniqueCandidates);
  };

  const profileImageCandidates = useMemo(() => {
    if (isCompany) {
      return getImageCandidates([company?.profileImage?.url]);
    }

    return getImageCandidates([
      user?.profilePictureUrl,
      user?.profileImage?.url,
      user?.instagramProfilePicture,
      user?.social?.instagram?.profilePictureUrl,
      user?.social?.instagram?.profileImage?.url,
    ]);
  }, [
    company?.profileImage?.url,
    isCompany,
    user?.instagramProfilePicture,
    user?.profileImage?.url,
    user?.profilePictureUrl,
    user?.social?.instagram?.profileImage?.url,
    user?.social?.instagram?.profilePictureUrl,
  ]);

  useEffect(() => {
    setProfileImageCandidateIndex(0);
  }, [user?._id, company?._id, profileImageCandidates]);

  const activeProfileImageUrl = profileImageCandidates[profileImageCandidateIndex];

  const displayName = useMemo(() => {
    if (isCompany) {
      return (
        company?.companyName ||
        [company?.firstName, company?.lastName].filter(Boolean).join(" ") ||
        company?.email ||
        "Company"
      );
    }

    return (
      user?.fullName ||
      user?.username ||
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      user?.name ||
      user?.email?.split("@")[0] ||
      "Influencer"
    );
  }, [company, isCompany, user]);

  const subtitle = isCompany ? company?.email : user?.email;
  const dashboardPath = isCompany ? "/company/dashboard" : "/influencer/dashboard";
  const profilePath = isCompany ? "/company/profile" : "/influencer/dashboard/influencer-profile";
  const settingsPath = isCompany
    ? "/company/dashboard/settings"
    : "/influencer/dashboard/settings";

  const closeAllMenus = () => {
    setIsOpen(false);
    if (closeMobileSidebar) {
      closeMobileSidebar();
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    closeAllMenus();
  };

  const handleLogout = async () => {
    try {
      if (isCompany) {
        await dispatch(logoutCompany()).unwrap();
      }
      dispatch(clearUserState());
      navigate("/");
      closeAllMenus();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const renderAvatar = () => {
    if (activeProfileImageUrl) {
      return (
        <img
          src={activeProfileImageUrl}
          alt="Profile"
          className="h-full w-full rounded-full object-cover"
          loading="lazy"
          onError={() => {
            setProfileImageCandidateIndex((prevIndex) => prevIndex + 1);
          }}
        />
      );
    }

    return displayName.charAt(0).toUpperCase();
  };

  if (!isLoggedIn) {
    return null;
  }

  const menuItems = [
    {
      key: "profile",
      label: "Profile",
      icon: User,
      onClick: () => handleNavigation(profilePath),
    },
    showDashboardAction && {
      key: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      onClick: () => handleNavigation(dashboardPath),
    },
    showSettingsAction && {
      key: "settings",
      label: "Settings",
      icon: Settings,
      onClick: () => handleNavigation(settingsPath),
    },
    {
      key: "logout",
      label: "Logout",
      icon: LogOut,
      onClick: handleLogout,
    },
  ].filter(Boolean);

  if (variant === "sidebar") {
    return (
      <div className={`relative ${className}`} ref={menuRef}>
        {isOpen ? (
          <div
            className={`mb-2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg ${
              collapsed
                ? "absolute bottom-full left-full z-50 mb-2 ml-2 w-48"
                : ""
            }`}
          >
            {menuItems.map(({ key, label, icon: Icon, onClick }) => (
              <button
                key={key}
                type="button"
                onClick={onClick}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
              >
                <Icon className="h-4 w-4 text-gray-500" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`flex w-full items-center rounded-2xl border border-gray-200 bg-white text-left text-gray-800 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 ${
            collapsed ? "justify-center p-1.5" : "gap-3 p-3"
          }`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          title={collapsed ? displayName : undefined}
        >
          <div
            className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 font-bold text-gray-700 ${
              collapsed ? "h-10 w-10 text-sm" : "h-12 w-12 text-base"
            }`}
          >
            {renderAvatar()}
          </div>
          {!collapsed ? (
            <>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-gray-800">{displayName}</div>
                {subtitle ? (
                  <div className="truncate text-xs text-gray-500">{subtitle}</div>
                ) : null}
              </div>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </>
          ) : null}
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        type="button"
        className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-orange-500 font-bold text-black transition-colors hover:bg-orange-600"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {renderAvatar()}
      </button>

      {isOpen ? (
        <div className="absolute right-0 mt-2 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg z-[60]">
          {menuItems.map(({ key, label, icon: Icon, onClick }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              className="flex w-full items-center px-4 py-3 text-gray-700 transition-colors hover:bg-gray-100"
            >
              <Icon className="mr-3 h-5 w-5" />
              <span className="text-sm md:text-base">{label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default UserProfileMenu;
